import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "linux",
  name: "Linux",
  nameEn: "Linux",
  seo: {
    title: "Linuxを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "Linuxはサーバー・クラウドの基盤となるOSです。ゆめさくエンジニアならコマンドライン操作からサーバー管理まで実践的なLinuxスキルを習得できます。",
    url: "/columns/linux-programming-school",
  },
  hero: {
    title: "Linuxを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Linux",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "Linux" },
  ],
  overview: {
    heading: "Linuxとは？",
    paragraphs: [
      "Linuxは1991年にリーナス・トーバルズによって開発されたオープンソースのオペレーティングシステム（OS）です。サーバー用途ではシェア90%以上を占め、Web・クラウドの世界を支える基盤技術です。",
      "AWS、Google Cloud、Azureなどの主要クラウドサービスはLinux上で稼働しており、Webサービスを運用するエンジニアにとってLinuxの知識は必須です。Android OSもLinuxカーネルをベースに作られています。",
      "コマンドラインを使った効率的な操作、シェルスクリプトによる自動化、パーミッション管理など、エンジニアの生産性を大きく高めるスキルが身につきます。",
    ],
    useCases: [
      {
        label: "Webサーバーの構築・管理",
        description:
          "Nginx・Apacheなどを使ったWebサーバーのセットアップ、SSL証明書の設定、ログ監視などサーバー運用全般を行います。",
      },
      {
        label: "シェルスクリプトによる業務自動化",
        description:
          "ファイル操作、ログ集計、バックアップ、デプロイなど定型作業をシェルスクリプトで自動化し、作業時間を大幅に削減します。",
      },
      {
        label: "開発環境の構築",
        description:
          "各種プログラミング言語・フレームワーク・データベースの開発環境をLinux上に構築します。Docker環境もLinuxの知識が土台になります。",
      },
      {
        label: "セキュリティ管理",
        description:
          "ファイアウォール設定、ユーザー権限管理、SSH鍵認証など、サーバーのセキュリティを確保するための対策を実施します。",
      },
    ],
  },
  whyLearn: {
    heading: "Linuxを学ぶべき3つの理由",
    items: [
      {
        title: "クラウド時代に必須の基盤スキル",
        description:
          "AWS・GCP・Azureなどクラウド環境の大半はLinuxで動いています。クラウドエンジニアやDevOpsを目指すなら、Linuxの操作は避けて通れない基礎スキルです。",
      },
      {
        title: "コマンドラインで生産性が飛躍的に向上",
        description:
          "GUIでは何十回もクリックが必要な作業が、コマンド一行で完了します。ファイル操作、テキスト処理、プロセス管理など、開発者としての生産性が格段に上がります。",
      },
      {
        title: "インフラエンジニアの高い年収と安定した需要",
        description:
          "Linux知識を持つインフラエンジニアやSREは慢性的に人手不足で、年収水準も高い傾向があります。安定した需要があり、長期的なキャリアを築きやすい分野です。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでLinuxを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではPhase 5の基礎としてLinuxを学びます。それまでに身につけたプログラミングスキルを土台に、開発したアプリケーションを実際のサーバー上で動かすためのインフラ知識を習得します。",
      "仮想マシンを使った安全な環境で実際にコマンドを打ちながら学ぶハンズオン形式のため、黒い画面に慣れていない方でも安心です。",
    ],
    topics: [
      "Linuxの基本概念・ディストリビューション",
      "ファイルシステムとディレクトリ操作",
      "ファイル権限・パーミッション管理",
      "プロセス管理・ジョブ制御",
      "シェルスクリプト入門（bash）",
      "SSH接続・サーバー管理の基礎",
    ],
  },
  careerPaths: {
    heading: "Linuxを活かせるキャリア",
    items: [
      {
        title: "インフラエンジニア",
        description:
          "サーバー・ネットワーク・ストレージなどITインフラの設計・構築・運用を担当します。システムの安定稼働を支える重要な役割で、24/365の可用性を求められる現場で活躍します。",
        salary: "450〜750万円",
      },
      {
        title: "SRE（Site Reliability Engineer）",
        description:
          "システムの信頼性・可用性を高めるためにソフトウェアエンジニアリングの手法をインフラ運用に適用します。監視・アラート設計、障害対応の自動化、パフォーマンスチューニングを行います。",
        salary: "600〜1,000万円",
      },
      {
        title: "DevOpsエンジニア",
        description:
          "開発チームと運用チームの橋渡し役として、CI/CDパイプラインの構築、インフラのコード化（IaC）、自動化の推進を行います。Linuxの深い知識が求められます。",
        salary: "550〜900万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "4〜8ヶ月（コマンドの暗記で終わりがち）",
        luagate: "2〜3ヶ月（実務シナリオで体系的に習得）",
      },
      {
        label: "挫折率",
        selfStudy: "黒い画面に慣れず早期に挫折する人が多い",
        luagate: "段階的なハンズオンで無理なくステップアップ",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "教材のコマンドをコピペするだけ",
        luagate: "実際にWebサーバーを構築・運用する実践課題",
      },
      {
        label: "質問対応",
        selfStudy: "エラーの原因特定に時間がかかる",
        luagate: "現役インフラエンジニアに直接質問可能",
      },
      {
        label: "就職サポート",
        selfStudy: "すべて自力で進める",
        luagate: "ポートフォリオ添削・面接対策まで一貫サポート",
      },
    ],
  },
  faq: [
    {
      question: "LinuxはWindowsやMacと何が違うのですか？",
      answer:
        "Linuxはオープンソースで無料、カスタマイズ性が高いのが特徴です。サーバー用途で圧倒的なシェアを持ち、WindowsやMacが主にデスクトップ用途で使われるのに対し、LinuxはWebサービスの裏側を支えるインフラとして広く使われています。",
    },
    {
      question: "自宅のパソコンでLinuxを学習できますか？",
      answer:
        "はい、仮想マシン（VirtualBox等）やWSL2（Windows）を使えば、今のパソコンの中にLinux環境を構築できます。ゆめさくエンジニアでは環境構築から丁寧にサポートするため、手順に迷う心配はありません。",
    },
    {
      question: "プログラミング経験がなくてもLinuxを学べますか？",
      answer:
        "ゆめさくエンジニアではPhase 1〜4でプログラミングの基礎を学んだ後にLinuxに入るため、前提知識は不要です。コマンドライン操作はプログラミングとは別のスキルなので、むしろ新鮮な気持ちで楽しく学ぶ方が多いです。",
    },
    {
      question: "LinuxとDockerの関係は？",
      answer:
        "DockerはLinuxの技術（コンテナ、名前空間、cgroups）を基盤にしています。Linuxの仕組みを理解していると、Dockerのトラブルシューティングやパフォーマンスチューニングがスムーズにできるようになります。",
    },
  ],
  relatedLanguages: [
    { slug: "aws", name: "AWS / クラウド" },
    { slug: "docker", name: "Docker / CI-CD" },
    { slug: "kubernetes", name: "Kubernetes" },
    { slug: "security", name: "セキュリティ" },
  ],
};
